import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Search, Sparkles, Flame, Star, Gem, Clock, SlidersHorizontal, Shuffle, ShieldCheck, Film, Heart, Play, Trophy, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { GameCard, Spinner, EmptyState } from "@/components/Bits";
import { TrustRing } from "@/components/TrustRing";
import RemixChallengeBanner from "@/components/RemixChallengeBanner";
import CommunityClips from "@/components/CommunityClips";
import { CardRow } from "@/components/PlayCard";
import WeeklyEventsStrip from "@/components/WeeklyEventsStrip";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GameTrailer } from "@/components/GameTrailer";
import { GenrePreview } from "@/components/GenrePreview";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const GENRES = ["All", "Platformer", "Fighting", "Shooter", "Battle Royale", "Sandbox", "Horror", "Sports", "Racing", "Story", "Music", "Adventure", "Puzzle", "Quiz", "Educational", "Card", "Reaction", "Kids"];
const AGES = ["All", "Everyone", "6+", "9+", "12+", "Teen"];
const SORTS = [["trending", "Trending"], ["new", "Newest"], ["top", "Top rated"]];

const RAILS = [
  ["hot", "🔥 On the rise", "Games smashing play milestones right now", Sparkles],
  ["for_you", "✨ For you", "Picked from what you play and like", Sparkles],
  ["top_rated", "⭐ Top rated", "Highest-rated games on GameView", Star],
  ["hidden_gems", "💎 Hidden gems", "Brilliant games that deserve more plays", Gem],
  ["new", "🆕 New this week", "Fresh from the community", Clock],
];

// 🔥 Featured genres — showcase the full range of engines. Each card deep-links into the
// AI creator with the idea pre-filled, turning "wow, it can make that?" into an instant build.
const SHOWCASE_GENRES = [
  { emoji: "🥊", name: "Fighting", tag: "Versus brawler, best-of-3", kind: "fighting", grad: "from-rose-500/25 to-red-600/10", idea: "a 2D versus fighting game with special moves and best-of-3 rounds" },
  { emoji: "🪂", name: "Battle Royale", tag: "Last one standing", kind: "battle", grad: "from-violet-500/25 to-fuchsia-600/10", idea: "a 3D battle royale with a shrinking safe zone and AI rivals" },
  { emoji: "🧱", name: "Sandbox", tag: "Voxel build & explore", kind: "sandbox", grad: "from-lime-500/25 to-green-600/10", idea: "a 3D voxel sandbox where you place and break blocks to build anything" },
  { emoji: "🔦", name: "Horror", tag: "First-person flashlight survival", kind: "horror", grad: "from-indigo-600/30 to-purple-900/10", idea: "a spooky first-person horror survival where you find items and escape a creature at night" },
  { emoji: "⚽", name: "Soccer", tag: "Arcade 2-v-2 sports", kind: "soccer", grad: "from-emerald-500/25 to-green-600/10", idea: "a 3D 2v2 arcade soccer match against an AI team" },
  { emoji: "📖", name: "Visual Novel", tag: "Branching, voiced story", kind: "visualnovel", grad: "from-purple-500/25 to-pink-600/10", idea: "a branching visual novel story with characters, dialogue and choices" },
  { emoji: "🎵", name: "Rhythm", tag: "Tap to the beat", kind: "rhythm", grad: "from-pink-500/25 to-rose-600/10", idea: "a neon music rhythm game where you hit falling notes to the beat" },
  { emoji: "🍄", name: "Platformer", tag: "Classic jump & run", kind: "platformer", grad: "from-sky-500/25 to-blue-600/10", idea: "a classic 2D platformer with jumping, coins and enemies" },
  { emoji: "🔫", name: "Shooter", tag: "First-person arena", kind: "shooter", grad: "from-orange-500/25 to-amber-600/10", idea: "a first-person team shooter in an arena" },
  { emoji: "🌆", name: "3D Open World", tag: "Open-world explorer", kind: "openworld", grad: "from-cyan-500/25 to-teal-600/10", idea: "a 3D open-world adventure to explore with quests" },
  { emoji: "🏁", name: "Racing", tag: "Kart circuit racer", kind: "racing", grad: "from-yellow-500/25 to-orange-600/10", idea: "a 3D kart racing game around a looping circuit collecting coins" },
  { emoji: "🏰", name: "Tower Defense", tag: "Defend the base", kind: "tower", grad: "from-slate-500/25 to-gray-600/10", idea: "a 3D tower defense game where you place towers to stop waves of enemies" },
];

export default function Discover() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [genre, setGenre] = useState("All");
  const [age, setAge] = useState("All");
  const [sort, setSort] = useState("trending");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [featured, setFeatured] = useState(null); // 🎬 featured-trailer spotlight
  const [demos, setDemos] = useState({}); // ⭐ staff-picked playable demo per genre
  const [trailerGames, setTrailerGames] = useState([]); // 🎬 games with a recorded cinematic trailer
  const [robotTop, setRobotTop] = useState([]); // 🤖 top-rated by the robot playtester
  const [certifiedCreators, setCertifiedCreators] = useState([]); // 🤖 creators with Certified-Fun games
  const [watch, setWatch] = useState(null); // 🎬 trailer lightbox (play full trailer with sound in-page)

  useEffect(() => { api.get("/discover").then((r) => setData(r.data)).catch(() => setData({})); }, []);
  useEffect(() => { api.get("/social/creators/certified", { params: { limit: 12 } }).then((r) => setCertifiedCreators(r.data || [])).catch(() => setCertifiedCreators([])); }, []);
  useEffect(() => { api.get("/arcade/verified", { params: { limit: 12 } }).then((r) => setVerified(r.data || [])).catch(() => setVerified([])); }, []);
  useEffect(() => { api.get("/arcade/trailers", { params: { limit: 12 } }).then((r) => setTrailerGames(r.data || [])).catch(() => setTrailerGames([])); }, []);
  useEffect(() => { api.get("/arcade/robot-top", { params: { limit: 12 } }).then((r) => setRobotTop(r.data || [])).catch(() => setRobotTop([])); }, []);
  useEffect(() => { api.get("/creators/verified-leaderboard", { params: { limit: 10 } }).then((r) => setLeaderboard(r.data || [])).catch(() => setLeaderboard([])); }, []);
  useEffect(() => { api.get("/tips/campaigns", { params: { limit: 12 } }).then((r) => setCampaigns(r.data?.campaigns || [])).catch(() => setCampaigns([])); }, []);
  useEffect(() => { api.get("/arcade/genre-demos").then((r) => setDemos(r.data?.demos || {})).catch(() => setDemos({})); }, []);

  const filtering = genre !== "All" || age !== "All" || !!search;
  useEffect(() => {
    if (!filtering) { setResults([]); return; }
    setLoading(true);
    const params = { sort, limit: 60 };
    if (genre !== "All") params.category = genre;
    if (age !== "All") params.age = age;
    if (search) params.search = search;
    const t = setTimeout(() => {
      api.get("/arcade", { params }).then((r) => { setResults(r.data); setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [genre, age, sort, search, filtering]);

  // Pick a featured game (with an AI soundtrack) for the top-of-page trailer spotlight.
  useEffect(() => {
    if (!data) return;
    const pool = [];
    ["hot", "for_you", "top_rated", "hidden_gems", "new"].forEach((k) => (data[k] || []).slice(0, 2).forEach((g) => pool.push(g)));
    const seen = new Set();
    const cands = [];
    pool.forEach((g) => { if (g && g.id && !seen.has(g.id)) { seen.add(g.id); cands.push(g); } });
    const top = cands.slice(0, 6);
    if (!top.length) { setFeatured(null); return; }
    let alive = true;
    Promise.all(top.map((g) => api.get(`/og/game/${g.id}/info`).then((r) => ({ g, info: r.data })).catch(() => null)))
      .then((res) => {
        if (!alive) return;
        const withBgm = res.filter((x) => x && x.info && x.info.found && x.info.bgm_url);
        setFeatured(withBgm[0] || null);
      });
    return () => { alive = false; };
  }, [data]);

  const open = (g) => navigate(`/app/arcade/${g.id}`);
  if (!data) return <Spinner />;
  return (
    <div className="p-5 lg:p-8 max-w-7xl mx-auto" data-testid="discover-page">
      <div className="flex items-center gap-3 mb-2">
        <Compass className="w-8 h-8 text-[hsl(var(--primary))]" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Discover</h1>
      </div>
      <p className="text-[hsl(var(--muted-foreground))] mb-6">Games chosen for you — plus powerful filters to find exactly what you're in the mood for.</p>

      {/* Filter bar */}
      <div className="sticky top-16 z-20 -mx-5 px-5 py-3 bg-[hsl(var(--background))]/80 backdrop-blur-xl">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search games…" className="pl-9" data-testid="discover-search" />
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <SlidersHorizontal className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
            <Select value={genre} onValueChange={setGenre}>
              <SelectTrigger className="w-36" data-testid="discover-genre"><SelectValue placeholder="Genre" /></SelectTrigger>
              <SelectContent>{GENRES.map((g) => <SelectItem key={g} value={g}>{g === "All" ? "All genres" : g}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={age} onValueChange={setAge}>
              <SelectTrigger className="w-32" data-testid="discover-age"><SelectValue placeholder="Age" /></SelectTrigger>
              <SelectContent>{AGES.map((a) => <SelectItem key={a} value={a}>{a === "All" ? "All ages" : `Ages ${a}`}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-36" data-testid="discover-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {data.interests?.length > 0 && !filtering && (
          <div className="flex items-center gap-2 mt-3 text-xs text-[hsl(var(--muted-foreground))]">
            <Flame className="w-3.5 h-3.5" /> Your vibe:
            {data.interests.map((c) => <Badge key={c} variant="secondary" className="capitalize cursor-pointer" onClick={() => setGenre(c.charAt(0).toUpperCase() + c.slice(1))}>{c}</Badge>)}
          </div>
        )}
      </div>

      {!filtering && <RemixChallengeBanner />}

      {!filtering && featured && (
        <div className="mb-8 mt-2" data-testid="discover-featured-trailer">
          <div className="flex items-center gap-2 mb-3">
            <Film className="w-5 h-5 text-[hsl(var(--primary))]" />
            <h2 className="font-display text-xl font-bold">🎬 Featured trailer</h2>
          </div>
          <div className="grid lg:grid-cols-[1.5fr_1fr] gap-5 items-center">
            <GameTrailer image={`${BACKEND}/api/og/game/${featured.g.id}/image.png`} bgmUrl={featured.info.bgm_url} emoji={featured.info.cover_emoji} testid="discover-trailer" />
            <div>
              <h3 className="font-display text-2xl font-extrabold">{featured.info.cover_emoji} {featured.info.title}</h3>
              <p className="text-[hsl(var(--muted-foreground))] mt-1">by {featured.info.owner_name}</p>
              {featured.info.description && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-3">{featured.info.description}</p>}
              <div className="flex gap-2 mt-4">
                <button onClick={() => open(featured.g)} data-testid="discover-trailer-play" className="rounded-full h-11 px-6 font-semibold text-white bg-gradient-to-r from-[#1f6feb] to-[#7c3aed] inline-flex items-center gap-2 hover:opacity-90 transition-opacity">
                  <Play className="w-4 h-4" /> Play now
                </button>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">▶ Tap the trailer to hear its AI soundtrack</p>
            </div>
          </div>
        </div>
      )}


      {!filtering && (
        <div className="mb-9" data-testid="featured-genres">
          <div className="flex items-center gap-2 mb-1">
            <Flame className="w-5 h-5 text-orange-500" />
            <h2 className="font-display text-xl font-bold">🔥 Featured genres</h2>
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Our AI builds any of these — tap one to start creating in seconds.</p>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x" style={{ scrollbarWidth: "thin" }}>
            {SHOWCASE_GENRES.map((g) => {
              const demo = demos[g.name];
              return (
              <div
                key={g.name}
                data-testid={`genre-card-${g.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => navigate(`/app/create?idea=${encodeURIComponent(g.idea)}`)}
                className={`group snap-start shrink-0 w-48 cursor-pointer text-left rounded-2xl border border-[hsl(var(--border))] bg-gradient-to-br ${g.grad} p-2.5 transition-transform duration-200 hover:-translate-y-1 hover:border-[hsl(var(--primary))]/60`}
              >
                <div className={`relative h-24 rounded-xl overflow-hidden mb-2 bg-gradient-to-br ${g.grad} ring-1 ring-black/5`}>
                  <GenrePreview kind={g.kind} />
                  <span className="absolute top-1.5 right-1.5 text-lg drop-shadow">{g.emoji}</span>
                  {demo && (
                    <button
                      data-testid={`genre-demo-${g.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={(e) => { e.stopPropagation(); navigate(`/app/arcade/${demo.id}`); }}
                      title={`Play demo: ${demo.title}`}
                      className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-full bg-black/70 backdrop-blur px-2 py-1 text-[10px] font-bold text-amber-300 border border-amber-300/40 hover:bg-black/85 hover:scale-105 transition-transform"
                    >
                      <Star className="w-3 h-3 fill-amber-300" /> Play demo
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between gap-1 px-1">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm truncate">{g.name}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] leading-tight truncate">{g.tag}</div>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-0.5 text-[11px] font-semibold text-[hsl(var(--primary))] opacity-0 group-hover:opacity-100 transition-opacity">
                    <Sparkles className="w-3 h-3" /> Create
                  </span>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}


      {filtering ? (
        <div className="mt-6">
          <h2 className="font-display text-xl font-bold mb-3">Results</h2>
          {loading ? <Spinner /> : results.length === 0 ? (
            <EmptyState emoji="🕹️" title="No games match" text="Try a different genre, age range, or search." />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="discover-results">
              {results.map((g) => <GameCard key={g.id} game={g} onClick={() => open(g)}
                badge={g.staff_pick ? "STAFF PICK" : g.featured ? "FEATURED" : g.has_premium ? "PREMIUM" : null} />)}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4" data-testid="discover-rails">
          <WeeklyEventsStrip />
          <button onClick={() => navigate("/app/challenge")} data-testid="discover-challenge-banner"
            className="w-full text-left mb-9 rounded-2xl border border-amber-400/30 bg-gradient-to-r from-amber-400/[0.10] via-fuchsia-500/[0.05] to-transparent p-4 flex items-center gap-4 card-hover">
            <div className="w-11 h-11 shrink-0 rounded-xl grid place-items-center bg-gradient-to-br from-amber-400 to-orange-500 text-black text-2xl">🏆</div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-wide text-amber-500">Imagine It. Build It.</div>
              <div className="font-display font-extrabold truncate">Enter the community build challenge</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Show off a game and vote for your favourites →</div>
            </div>
          </button>
          {data.creator_of_week && (
            <CreatorOfWeekSpotlight cow={data.creator_of_week}
              onOpen={() => navigate(`/app/creators/${data.creator_of_week.owner_id}`)}
              onPlay={(gid) => navigate(`/app/arcade/${gid}`)}
              onSeeAll={() => navigate("/app/creator-race")} />
          )}
          {trailerGames.length > 0 && <TrailerOfTheDay games={trailerGames} onWatch={setWatch} />}
          {robotTop.length > 0 && <CardRow title="🤖 Top-Rated by the Robot" games={robotTop} onPlay={open} testid="rail-robot-top" />}
          {certifiedCreators.length > 0 && (
            <CertifiedCreatorsRail creators={certifiedCreators}
              onOpen={(c) => navigate(`/app/creators/${c.id}`)}
              onPlay={(gid) => navigate(`/app/arcade/${gid}`)} />
          )}
          {trailerGames.length > 0 && <TrailerRail games={trailerGames} onWatch={setWatch} />}
          <CommunityClips featured title="⭐ Staff Pick Clips" sort="trending" limit={12} testid="discover-staff-picks" />
          {campaigns.length > 0 && (
            <SupportCampaignsRail campaigns={campaigns} onOpen={open} />
          )}
          {leaderboard.length > 0 && (
            <VerifiedLeaderboard creators={leaderboard} onOpen={(c) => navigate(`/app/creators/${c.id}`)} />
          )}
          {verified.length > 0 && (
            <VerifiedRail games={verified} onOpen={open} />
          )}
          {data.featured_creators?.length > 0 && (
            <FeaturedCreatorsRail creators={data.featured_creators} onOpen={(c) => navigate(`/app/creators/${c.owner_id}`)} />
          )}
          {data.rising_creators?.length > 0 && (
            <RisingCreatorsRail creators={data.rising_creators} onOpen={(c) => navigate(`/app/creators/${c.owner_id}`)} />
          )}
          {data.marketplace_trending?.length > 0 && (
            <MarketplaceTrendingRail items={data.marketplace_trending}
              onOpen={(l) => navigate(`/app/marketplace?listing=${l.id}`)}
              onSeeAll={() => navigate("/app/marketplace")} />
          )}
          {data.because?.items?.length > 0 && (
            <Rail title={`Because you played ${data.because.seed}`} subtitle="More like it" games={data.because.items} onOpen={open} testid="rail-because" />
          )}
          {data.top_remixed_creators?.length > 0 && (
            <RemixCreatorsRail creators={data.top_remixed_creators} onOpen={(c) => navigate(`/app/creators/${c.owner_id}`)} />
          )}
          {data.hot_clip_creators?.length > 0 && (
            <HotClipCreatorsRail creators={data.hot_clip_creators} onOpen={(c) => navigate(`/app/creators/${c.owner_id}`)} />
          )}
          {RAILS.map(([key, title, sub]) => (data[key]?.length > 0 ? (
            <Rail key={key} title={title} subtitle={sub} games={data[key]} onOpen={open} testid={`rail-${key}`} />
          ) : null))}
          {RAILS.every(([k]) => !data[k]?.length) && !data.because && (
            <EmptyState emoji="🧭" title="Nothing to recommend yet" text="Play and like a few games and we'll tailor recommendations just for you." />
          )}
        </div>
      )}

      <Dialog open={!!watch} onOpenChange={(o) => !o && setWatch(null)}>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden bg-black border-[hsl(var(--border))]" data-testid="trailer-lightbox">
          {watch && (
            <>
              <DialogTitle className="sr-only">{watch.title} — trailer</DialogTitle>
              <video src={`${BACKEND}${watch.trailer_clip_url}`} autoPlay loop controls playsInline
                className="w-full bg-black" style={{ aspectRatio: "16/9" }} data-testid="trailer-lightbox-video" />
              <div className="p-4 flex items-center justify-between gap-3 bg-[hsl(var(--card))]">
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{watch.cover_emoji} {watch.title}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">by {watch.owner_name}</div>
                </div>
                <button onClick={() => { const g = watch; setWatch(null); open(g); }} data-testid="trailer-lightbox-play"
                  className="shrink-0 rounded-full h-10 px-5 font-semibold text-white bg-gradient-to-r from-[#1f6feb] to-[#7c3aed] inline-flex items-center gap-2 hover:opacity-90 transition-opacity">
                  <Play className="w-4 h-4" /> Play now
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Rail({ title, subtitle, games, onOpen, testid }) {
  return (
    <div className="mb-7" data-testid={testid}>
      <div className="mb-3"><h2 className="font-display text-xl font-bold">{title}</h2>{subtitle && <p className="text-xs text-[hsl(var(--muted-foreground))]">{subtitle}</p>}</div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {games.map((g) => (
          <div key={g.id} className="w-48 shrink-0 snap-start">
            <GameCard game={g} onClick={() => onOpen(g)} badge={g.last_milestone ? `🔥 ${g.last_milestone >= 1000 ? g.last_milestone / 1000 + "K" : g.last_milestone} plays` : g.staff_pick ? "STAFF" : g.featured ? "FEATURED" : g.has_premium ? "PREMIUM" : null} />
          </div>
        ))}
      </div>
    </div>
  );
}

function MarketplaceTrendingRail({ items, onOpen, onSeeAll }) {
  return (
    <div className="mb-7" data-testid="rail-marketplace-trending">
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-fuchsia-400" /> Trending in the Marketplace</h2>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Creator assets, skins & worlds players are grabbing right now</p>
        </div>
        <button onClick={onSeeAll} data-testid="marketplace-trending-see-all"
          className="shrink-0 text-xs font-semibold text-[hsl(var(--primary))] hover:underline">Browse all →</button>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {items.map((l) => (
          <button key={l.id} onClick={() => onOpen(l)} data-testid={`market-trend-${l.id}`}
            className="group w-40 shrink-0 snap-start text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden hover:border-fuchsia-500/50 hover:-translate-y-0.5 transition-all">
            <div className="aspect-square grid place-items-center text-5xl bg-gradient-to-br from-fuchsia-500/15 to-[#3b9eff]/15 relative overflow-hidden">
              {l.preview_image
                ? <img src={l.preview_image.startsWith("http") ? l.preview_image : `${BACKEND}${l.preview_image}`} alt={l.title} className="w-full h-full object-cover" loading="lazy" />
                : <span>{l.preview_emoji}</span>}
              {l.sales > 0 && <span className="absolute top-1.5 left-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/55 text-amber-300 backdrop-blur">🔥 {l.sales} sold</span>}
            </div>
            <div className="p-2.5">
              <div className="text-sm font-semibold truncate leading-snug">{l.title}</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">by {l.seller_name}</div>
              <div className="flex items-center justify-between mt-1.5">
                <span className={`text-sm font-extrabold ${l.price > 0 ? "text-[hsl(var(--primary))]" : "text-emerald-500"}`}>{l.price > 0 ? `$${Number(l.price).toFixed(2)}` : "Free"}</span>
                {l.rating_count > 0 && <span className="flex items-center gap-0.5 text-[11px] text-[hsl(var(--muted-foreground))]"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {Number(l.rating_avg).toFixed(1)}</span>}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function SupportCampaignsRail({ campaigns, onOpen }) {
  const usd = (n) => `$${Number(n || 0).toFixed(0)}`;
  return (
    <div className="mb-7" data-testid="rail-support-campaigns">
      <div className="mb-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Heart className="w-5 h-5 text-pink-500 fill-pink-500" /> 🎯 Games seeking support</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Creator funding goals you can help close — every tip counts</p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {campaigns.map((c) => (
          <button key={c.id} onClick={() => onOpen(c)} data-testid={`campaign-${c.id}`}
            className="w-56 shrink-0 snap-start rounded-2xl border border-pink-500/30 bg-gradient-to-br from-pink-500/[0.07] to-transparent p-3 text-left hover:border-pink-500/60 transition-colors">
            <div className="flex items-center gap-2.5">
              <div className="w-11 h-11 rounded-xl grid place-items-center text-2xl bg-gradient-to-br from-pink-500/20 to-rose-500/20 shrink-0 overflow-hidden">
                {c.cover_image ? <img src={`${process.env.REACT_APP_BACKEND_URL}${c.cover_image}`} alt={c.title} className="w-full h-full object-cover" /> : c.cover_emoji}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm truncate">{c.title}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">by {c.owner_name}</div>
              </div>
            </div>
            <div className="mt-2.5 text-xs font-semibold truncate flex items-center gap-1"><span className="text-pink-500">🎯</span> {c.goal_title}</div>
            <div className="mt-1.5 h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-pink-500 to-rose-400" style={{ width: `${Math.max(3, c.progress_pct || 0)}%` }} data-testid={`campaign-bar-${c.id}`} />
            </div>
            <div className="flex items-center justify-between mt-1 text-[11px]">
              <span className="font-bold text-pink-500 tabular-nums">{usd(c.raised)} <span className="text-[hsl(var(--muted-foreground))] font-normal">of {usd(c.goal_amount)}</span></span>
              <span className="text-[hsl(var(--muted-foreground))]">{c.reached ? "🎉 funded" : `${Math.round(c.progress_pct)}%`}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function VerifiedLeaderboard({ creators, onOpen }) {  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div className="mb-7" data-testid="rail-verified-leaderboard">
      <div className="mb-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-500" /> Top Verified Creators</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Ranked by Creator Trust Score — publish clean, original games to climb</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {creators.map((c, i) => (
          <button key={c.id} onClick={() => onOpen(c)} data-testid={`leaderboard-creator-${c.id}`}
            className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-2.5 text-left hover:border-emerald-500/40 transition-colors">
            <span className="w-7 text-center font-display font-extrabold text-[hsl(var(--muted-foreground))] shrink-0">{medals[i] || `#${i + 1}`}</span>
            <span className="w-10 h-10 rounded-xl grid place-items-center text-xl bg-gradient-to-br from-[#7c3aed] to-[#3b9eff] shrink-0">{c.avatar_emoji}</span>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm truncate flex items-center gap-1">{c.name}<ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" /></div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{c.verified_games}/{c.games_count} verified · {c.total_plays} plays</div>
            </div>
            <TrustRing score={c.trust_score} size={40} />
          </button>
        ))}
      </div>
    </div>
  );
}



function VerifiedRail({ games, onOpen }) {
  return (
    <div className="mb-7" data-testid="rail-verified">
      <div className="mb-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-500" /> Verified originals</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Freshly published, IP-clean creations with a Certificate of Authenticity</p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {games.map((g) => (
          <div key={g.id} className="w-48 shrink-0 snap-start">
            <GameCard game={g} onClick={() => onOpen(g)} />
          </div>
        ))}
      </div>
    </div>
  );
}


function CreatorOfWeekSpotlight({ cow, onOpen, onPlay, onSeeAll }) {
  return (
    <div className="mb-7 rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-400/[0.10] via-orange-500/[0.06] to-transparent p-5 sm:p-6" data-testid="creator-of-week-spotlight">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 text-xs font-bold text-amber-500 uppercase tracking-wide">
          <Trophy className="w-4 h-4 fill-amber-400" /> Creator of the Week
        </div>
        <button onClick={onSeeAll} data-testid="cow-see-ranking" className="text-xs font-semibold text-amber-500 hover:underline">Full ranking →</button>
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <button onClick={onOpen} data-testid="cow-avatar"
          className="w-20 h-20 shrink-0 rounded-2xl grid place-items-center text-4xl bg-gradient-to-br from-amber-400/25 to-fuchsia-500/25 border border-amber-400/40 hover:scale-105 transition-transform">
          {cow.avatar_emoji}
        </button>
        <div className="flex-1 min-w-0">
          <button onClick={onOpen} className="font-display text-2xl font-extrabold hover:underline text-left" data-testid="cow-name">{cow.owner_name}</button>
          {cow.tier_label && <div className="text-sm font-semibold" style={{ color: cow.tier_color }}>{cow.tier_emoji} {cow.tier_label}</div>}
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-[hsl(var(--muted-foreground))]">
            <span className="flex items-center gap-1"><Play className="w-3.5 h-3.5" /> {cow.plays_7d.toLocaleString()} plays this week</span>
            {cow.followers_7d > 0 && <span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> {cow.followers_7d} new follower{cow.followers_7d === 1 ? "" : "s"}</span>}
            {cow.tips_7d > 0 && <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> ${cow.tips_7d.toFixed(0)} in tips</span>}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {cow.game && (
            <button onClick={() => onPlay(cow.game.id)} data-testid="cow-play-game"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-black hover:opacity-90 transition-opacity">
              <Play className="w-4 h-4" /> {cow.game.cover_emoji} Play {cow.game.title}
            </button>
          )}
          <button onClick={onOpen} data-testid="cow-view-profile"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border border-amber-400/40 text-amber-500 hover:bg-amber-400/10 transition-colors">
            View profile
          </button>
        </div>
      </div>
    </div>
  );
}

function RisingCreatorsRail({ creators, onOpen }) {
  return (
    <div className="mb-7" data-testid="rail-rising-creators">
      <div className="mb-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Flame className="w-5 h-5 text-orange-400" /> Rising this week</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Creators with big momentum right behind the Creator of the Week — tap to follow</p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {creators.map((c) => (
          <button key={c.owner_id} onClick={() => onOpen(c)} data-testid={`rising-creator-${c.owner_id}`}
            className="w-40 shrink-0 snap-start rounded-2xl border border-orange-400/25 bg-gradient-to-b from-orange-400/[0.07] to-transparent p-4 text-center card-hover">
            <div className="relative w-16 h-16 mx-auto rounded-full grid place-items-center text-3xl bg-gradient-to-br from-orange-400/20 to-fuchsia-500/25 border border-orange-400/30">
              {c.avatar_emoji}
              <span className="absolute -top-1 -right-1 text-base"><Flame className="w-4 h-4 text-orange-400 fill-orange-400" /></span>
            </div>
            <div className="mt-2 font-bold text-sm truncate">{c.owner_name}</div>
            {c.tier_label && <div className="text-[11px] font-semibold truncate" style={{ color: c.tier_color }}>{c.tier_label}</div>}
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
              {c.plays_7d > 0 && <span>{c.plays_7d} play{c.plays_7d === 1 ? "" : "s"}</span>}
              {c.followers_7d > 0 && <span> · +{c.followers_7d} 👥</span>}
              {c.tips_7d > 0 && <span> · ${c.tips_7d.toFixed(0)} 💚</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function FeaturedCreatorsRail({ creators, onOpen }) {
  return (
    <div className="mb-7" data-testid="rail-featured-creators">
      <div className="mb-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Star className="w-5 h-5 text-amber-400 fill-amber-400" /> Featured creators</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Gold-tier and above — creators who earned a spot on the Discover wall</p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {creators.map((c) => (
          <button key={c.owner_id} onClick={() => onOpen(c)} data-testid={`featured-creator-${c.owner_id}`}
            className="w-44 shrink-0 snap-start rounded-2xl border border-amber-400/25 bg-gradient-to-b from-amber-400/[0.07] to-transparent p-4 text-center card-hover relative">
            {c.promoted && (
              <span className="absolute top-2 left-2 inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-400 border border-orange-400/30" data-testid={`featured-rising-tag-${c.owner_id}`}>
                <Flame className="w-2.5 h-2.5 fill-orange-400" /> RISING STAR
              </span>
            )}
            <div className="relative w-16 h-16 mx-auto rounded-full grid place-items-center text-3xl bg-gradient-to-br from-amber-400/20 to-fuchsia-500/25 border border-amber-400/30">
              {c.avatar_emoji}
              <span className="absolute -top-1 -right-1 text-lg" title={c.tier_label}>{c.tier_emoji}</span>
            </div>
            <div className="mt-2 font-bold text-sm truncate">{c.owner_name}</div>
            <div className="text-[11px] font-semibold mt-0.5 truncate" style={{ color: c.tier_color }}>{c.tier_label}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{c.plays.toLocaleString()} plays · {c.games} game{c.games === 1 ? "" : "s"}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function CertifiedCreatorsRail({ creators, onOpen, onPlay }) {
  return (
    <div className="mb-7" data-testid="rail-certified-creators">
      <div className="mb-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-violet-400" /> Certified Creators</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Creators whose games the AI robot certified as Fun (80+) — tap to explore or play</p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {creators.map((c) => (
          <div key={c.id} data-testid={`certified-creator-${c.id}`}
            className="w-48 shrink-0 snap-start rounded-2xl border border-violet-500/25 bg-gradient-to-b from-violet-500/[0.08] to-transparent p-4 text-center card-hover">
            <button onClick={() => onOpen(c)} className="w-full">
              <div className="relative w-16 h-16 mx-auto rounded-full grid place-items-center text-3xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/25 border border-violet-500/30">
                {c.avatar_emoji}
                <span className="absolute -top-1 -right-1 text-base">🤖</span>
              </div>
              <div className="mt-2 font-bold text-sm truncate">{c.name}</div>
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-500 dark:text-violet-300 border border-violet-500/40">
                <ShieldCheck className="w-3 h-3" /> {c.certified_games} Certified · best {c.best_score}
              </div>
            </button>
            {c.top_game && (
              <button onClick={() => onPlay(c.top_game.id)} data-testid={`certified-creator-play-${c.id}`}
                className="mt-2 w-full flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-2 py-1.5 text-xs font-semibold hover:border-violet-500/50 transition-colors">
                <span className="text-base shrink-0">{c.top_game.cover_emoji || "🎮"}</span>
                <span className="truncate">{c.top_game.title}</span>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RemixCreatorsRail({ creators, onOpen }) {
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div className="mb-7" data-testid="rail-top-remixed">
      <div className="mb-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Shuffle className="w-5 h-5 text-[hsl(var(--primary))]" /> Top remixed creators</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Whose AI characters the community loves to remix</p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {creators.map((c, i) => (
          <button key={c.owner_id} onClick={() => onOpen(c)} data-testid={`remix-creator-${c.owner_id}`}
            className="w-40 shrink-0 snap-start rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 text-center card-hover">
            <div className="relative w-16 h-16 mx-auto rounded-full grid place-items-center text-3xl bg-gradient-to-br from-[#1f6feb]/20 to-[#7c3aed]/25 border border-[hsl(var(--border))]">
              {c.avatar_emoji}
              {i < 3 && <span className="absolute -top-1 -right-1 text-lg">{medals[i]}</span>}
            </div>
            <div className="mt-2 font-bold text-sm truncate">{c.owner_name}</div>
            <div className="text-xs text-[hsl(var(--primary))] font-semibold flex items-center justify-center gap-1 mt-0.5"><Shuffle className="w-3 h-3" /> {c.remixes} remix{c.remixes === 1 ? "" : "es"}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function HotClipCreatorsRail({ creators, onOpen }) {
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div className="mb-7" data-testid="rail-hot-clip-creators">
      <div className="mb-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Film className="w-5 h-5 text-fuchsia-400" /> Creators with hot clips</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Whose gameplay clips are trending this week — tap to follow</p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {creators.map((c, i) => (
          <button key={c.owner_id} onClick={() => onOpen(c)} data-testid={`hot-clip-creator-${c.owner_id}`}
            className="w-40 shrink-0 snap-start rounded-2xl border border-fuchsia-500/25 bg-fuchsia-500/[0.05] p-4 text-center card-hover">
            <div className="relative w-16 h-16 mx-auto rounded-full grid place-items-center text-3xl bg-gradient-to-br from-fuchsia-500/20 to-[#7c3aed]/25 border border-[hsl(var(--border))]">
              {c.avatar_emoji}
              {i < 3 && <span className="absolute -top-1 -right-1 text-lg">{medals[i]}</span>}
            </div>
            <div className="mt-2 font-bold text-sm truncate">{c.owner_name}</div>
            <div className="text-xs text-fuchsia-400 font-semibold flex items-center justify-center gap-1 mt-0.5"><Film className="w-3 h-3" /> {c.clips} clip{c.clips === 1 ? "" : "s"} trending</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function TrailerOfTheDay({ games, onWatch }) {
  const idx = useMemo(() => {
    const day = Math.floor(Date.now() / 86400000);
    return games.length ? day % games.length : 0;
  }, [games.length]);
  const g = games[idx];
  const gid = g ? g.id : null;
  const vref = useRef(null);
  useEffect(() => {
    const v = vref.current;
    if (v) { try { v.muted = true; const p = v.play(); if (p && p.catch) p.catch(() => {}); } catch (e) { /* ignore */ } }
  }, [gid]);
  if (!g) return null;
  return (
    <div className="mb-7 relative rounded-3xl overflow-hidden border border-amber-500/30 shadow-lg shadow-amber-500/10" data-testid="trailer-of-the-day">
      <div className="relative aspect-[21/9] bg-black">
        <video ref={vref} src={`${BACKEND}${g.trailer_clip_url}`} muted loop playsInline autoPlay preload="metadata"
          className="absolute inset-0 w-full h-full object-cover" data-testid="trailer-of-the-day-video" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
        <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-black px-3 py-1 text-xs font-black shadow"><Film className="w-3.5 h-3.5" /> Trailer of the Day</div>
        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7">
          <div className="font-display text-2xl sm:text-4xl font-extrabold text-white drop-shadow">{g.cover_emoji} {g.title}</div>
          <div className="text-white/70 text-sm mt-1">by {g.owner_name}</div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => onWatch(g)} data-testid="trailer-of-the-day-watch"
              className="rounded-full h-11 px-6 font-bold text-black bg-amber-400 hover:bg-amber-300 inline-flex items-center gap-2 transition-colors">
              <Play className="w-5 h-5 fill-black" /> Watch trailer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrailerRail({ games, onWatch }) {
  return (
    <div className="mb-7" data-testid="rail-trailers">
      <div className="mb-3">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Film className="w-5 h-5 text-amber-500" /> 🎬 Watch the trailer</h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Cinematic previews from 3D creators — hover to peek, tap to watch with sound</p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {games.map((g) => <TrailerCard key={g.id} g={g} onWatch={onWatch} />)}
      </div>
    </div>
  );
}

function TrailerCard({ g, onWatch }) {
  const vref = useRef(null);
  const [hover, setHover] = useState(false);
  const enter = () => {
    setHover(true);
    const v = vref.current;
    if (v) { try { v.currentTime = 0; const p = v.play(); if (p && p.catch) p.catch(() => {}); } catch (e) { /* ignore */ } }
  };
  const leave = () => {
    setHover(false);
    const v = vref.current;
    if (v) { try { v.pause(); v.currentTime = 0; } catch (e) { /* ignore */ } }
  };
  return (
    <button onClick={() => onWatch(g)} onMouseEnter={enter} onMouseLeave={leave} data-testid={`trailer-card-${g.id}`}
      className="group w-64 shrink-0 snap-start rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.06] to-transparent overflow-hidden text-left hover:border-amber-500/60 hover:-translate-y-1 transition-all duration-200">
      <div className="relative aspect-video bg-black overflow-hidden">
        {g.cover_image
          ? <img src={`${BACKEND}${g.cover_image}`} alt={g.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          : <div className="absolute inset-0 grid place-items-center text-5xl bg-gradient-to-br from-[#1f6feb]/20 to-[#7c3aed]/25">{g.cover_emoji}</div>}
        <video ref={vref} src={`${BACKEND}${g.trailer_clip_url}`} muted loop playsInline preload="none"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${hover ? "opacity-100" : "opacity-0"}`} data-testid={`trailer-card-video-${g.id}`} />
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/70 backdrop-blur px-2 py-0.5 text-[10px] font-bold text-amber-300 border border-amber-300/40">
          <Film className="w-3 h-3" /> Trailer
        </span>
        {!hover && (
          <span className="absolute inset-0 grid place-items-center pointer-events-none">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-black/55 backdrop-blur border border-white/25 text-white group-hover:scale-110 transition-transform">
              <Play className="w-5 h-5 fill-white" />
            </span>
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="font-display font-bold text-sm truncate">{g.cover_emoji} {g.title}</div>
        <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">by {g.owner_name}</div>
      </div>
    </button>
  );
}

